import {
    type ReactNode,
    useState
} from "react";
import {
    useWindowDimensions,
    type ViewStyle,
    StyleSheet,
    View
} from "react-native";
import {
    WorkspaceSwitcher
} from "./brand";
import {
    type NCoreUIKitIcon,
    NCoreUIKitLocalize,
    type ChartConfig,
    NCoreUIKitTheme,
    Breadcrumb,
    AreaChart,
    TextInput,
    Separator,
    StatTile,
    Progress,
    BarChart,
    PieChart,
    Sidebar,
    Avatar,
    Button,
    Badge,
    Table,
    Chart,
    Card,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    EllipsisVertical,
    LayoutDashboard,
    TrendingDown,
    ChartColumn,
    TrendingUp,
    Calendar,
    FileText,
    Target,
    Search,
    Users,
    Globe,
    Plus,
    Bell,
    Zap
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

type RangeKey = "last90" | "last30" | "last7";

type PageRow = {
    visitors: number;
    seconds: number;
    bounce: number;
    trend: number;
    path: string;
};

const LayoutDashboardIcon = createKitIcon(LayoutDashboard);

const EllipsisVerticalIcon = createKitIcon(EllipsisVertical);

const TrendingDownIcon = createKitIcon(TrendingDown);

const ChartColumnIcon = createKitIcon(ChartColumn);

const TrendingUpIcon = createKitIcon(TrendingUp);

const CalendarIcon = createKitIcon(Calendar);

const FileTextIcon = createKitIcon(FileText);

const TargetIcon = createKitIcon(Target);

const SearchIcon = createKitIcon(Search);

const UsersIcon = createKitIcon(Users);

const GlobeIcon = createKitIcon(Globe);

const PlusIcon = createKitIcon(Plus);

const ZapIcon = createKitIcon(Zap);

const BellIcon = createKitIcon(Bell);

const RANGES: Array<RangeKey> = [
    "last7",
    "last30",
    "last90"
];

const RANGE_DAYS: Record<RangeKey, number> = {
    last30: 30,
    last90: 90,
    last7: 7
};

const LAST_DAY = new Date(2026, 9, 31);

const DAY = 86400000;

const SOURCES = [
    {
        source: "blocks.dashboard01.sourceOrganic",
        sessions: 18940
    },
    {
        source: "blocks.dashboard01.sourceDirect",
        sessions: 12380
    },
    {
        source: "blocks.dashboard01.sourceSocial",
        sessions: 7420
    },
    {
        source: "blocks.dashboard01.sourceEmail",
        sessions: 5210
    },
    {
        source: "blocks.dashboard01.sourceReferral",
        sessions: 4260
    }
];

const DEVICES = [
    {
        name: "mobile",
        value: 27962
    },
    {
        name: "desktop",
        value: 16391
    },
    {
        name: "tablet",
        value: 3857
    }
];

const PAGES: Array<PageRow> = [
    {
        visitors: 14820,
        bounce: 0.314,
        seconds: 168,
        trend: 0.082,
        path: "/"
    },
    {
        path: "/pricing",
        visitors: 9436,
        bounce: 0.279,
        seconds: 185,
        trend: 0.146
    },
    {
        path: "/features/reports",
        visitors: 6212,
        bounce: 0.332,
        seconds: 261,
        trend: 0.05
    },
    {
        path: "/blog/analytics-guide",
        visitors: 4870,
        bounce: 0.448,
        seconds: 312,
        trend: -0.037
    },
    {
        path: "/login",
        visitors: 3945,
        bounce: 0.221,
        seconds: 96,
        trend: 0.019
    },
    {
        path: "/docs/getting-started",
        visitors: 2318,
        bounce: 0.486,
        seconds: 362,
        trend: -0.064
    }
];

const stylesheet = StyleSheet.create({
    bar: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    group: {
        alignItems: "center",
        flexDirection: "row",
        flexShrink: 1
    },
    row: {
        flexDirection: "row"
    },
    chart: {
        width: "100%",
        flexGrow: 1
    },
    search: {
        minWidth: 0,
        flex: 1
    },
    tile: {
        minWidth: 0,
        flex: 1
    },
    wide: {
        minWidth: 0,
        flex: 2
    },
    narrow: {
        minWidth: 0,
        flex: 1
    },
    grow: {
        flexGrow: 1
    },
    content: {
        minWidth: 0
    }
});

const handlePress = () => undefined;

const Dashboard01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        activePage,
        setActivePage
    ] = useState("analytics");

    const [
        range,
        setRange
    ] = useState<RangeKey>("last30");

    const integer = new Intl.NumberFormat(activeLocale);

    const percent = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
        style: "percent"
    });

    const signedPercent = new Intl.NumberFormat(activeLocale, {
        signDisplay: "exceptZero",
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
        style: "percent"
    });

    const minuteFormat = new Intl.NumberFormat(activeLocale, {
        unitDisplay: "narrow",
        style: "unit",
        unit: "minute"
    });

    const secondFormat = new Intl.NumberFormat(activeLocale, {
        minimumIntegerDigits: 2,
        unitDisplay: "narrow",
        style: "unit",
        unit: "second"
    });

    const dayFormat = new Intl.DateTimeFormat(activeLocale, {
        month: "short",
        day: "numeric"
    });

    const formatDuration = (seconds: number) => `${minuteFormat.format(Math.floor(seconds / 60))} ${secondFormat.format(seconds % 60)}`;

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const isSplit = width >= shapeConfigs.wideBreakpoint;

    const isRoomy = width >= shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth;

    const columnCount = isCompact ? 1 : isRoomy ? 4 : 2;

    const dayCount = RANGE_DAYS[range];

    const chartRows = Array.from({
        length: dayCount
    }, (_, index) => ({
        desktop: Math.round(470 + index * 3 + 45 * Math.sin(index * 0.38 + 2) + 16 * Math.sin(index * 0.9)),
        mobile: Math.round(820 + index * 7 + 70 * Math.sin(index * 0.38) + 26 * Math.sin(index * 0.9 + 1)),
        date: new Date(LAST_DAY.getTime() - (dayCount - 1 - index) * DAY).toISOString()
    }));

    const tiles = [
        {
            title: t("blocks.dashboard01.visitors"),
            value: integer.format(48210),
            isIncreaseGood: true,
            id: "visitors",
            delta: 0.124,
            trend: [
                30,
                32,
                31,
                35,
                38,
                37,
                41,
                44,
                43,
                48,
                52,
                55
            ]
        },
        {
            title: t("blocks.dashboard01.duration"),
            value: formatDuration(222),
            isIncreaseGood: true,
            id: "duration",
            delta: 0.031,
            trend: [
                40,
                41,
                39,
                42,
                43,
                42,
                44,
                45,
                44,
                46,
                46,
                47
            ]
        },
        {
            title: t("blocks.dashboard01.bounce"),
            value: percent.format(0.382),
            isIncreaseGood: false,
            delta: -0.02,
            id: "bounce",
            trend: [
                46,
                45,
                46,
                44,
                43,
                44,
                42,
                41,
                41,
                40,
                39,
                38
            ]
        },
        {
            title: t("blocks.dashboard01.conversion"),
            value: percent.format(0.046),
            isIncreaseGood: true,
            id: "conversion",
            delta: -0.004,
            trend: [
                52,
                54,
                53,
                55,
                53,
                52,
                51,
                52,
                50,
                49,
                49,
                48
            ]
        }
    ];

    const tileRows = Array.from({
        length: Math.ceil(tiles.length / columnCount)
    }, (_, index) => tiles.slice(index * columnCount, (index + 1) * columnCount));

    const visitorConfig: ChartConfig = {
        desktop: {
            title: t("blocks.dashboard01.desktop"),
            colorIndex: 1
        },
        mobile: {
            title: t("blocks.dashboard01.mobile"),
            colorIndex: 2
        }
    };

    const deviceConfig: ChartConfig = {
        desktop: {
            title: t("blocks.dashboard01.desktop"),
            colorIndex: 1
        },
        mobile: {
            title: t("blocks.dashboard01.mobile"),
            colorIndex: 2
        },
        tablet: {
            title: t("blocks.dashboard01.tablet"),
            colorIndex: 3
        }
    };

    const sourceConfig: ChartConfig = {
        sessions: {
            title: t("blocks.dashboard01.sessions"),
            colorIndex: 1
        }
    };

    const handleRangeChange = (value: string) => setRange(RANGES.find((key) => key === value) ?? "last30");

    const handlePage = (page: string) => () => setActivePage(page);

    const renderNavItem = (page: string, icon: NCoreUIKitIcon, title: string, count?: number) => <Sidebar.MenuItem
        key={page}
    >
        <Sidebar.MenuButton
            isActive={activePage === page}
            onPress={handlePage(page)}
            title={title}
            icon={icon}
        />
        {
            count ?
                <Sidebar.MenuBadge
                    count={count}
                />
            :
                null
        }
    </Sidebar.MenuItem>;

    const renderTile = (tile: typeof tiles[number]) => <StatTile
        style={stylesheet.tile}
        isTrendFlush={true}
        trend={tile.trend}
        variant="outline"
        key={tile.id}
    >
        <StatTile.Title>
            {tile.title}
        </StatTile.Title>
        <StatTile.Value>
            {tile.value}
        </StatTile.Value>
        <StatTile.Delta
            description={t("blocks.dashboard01.vsPrevious")}
            isIncreaseGood={tile.isIncreaseGood}
            value={tile.delta}
        />
    </StatTile>;

    const renderPage = (row: PageRow) => <Table.Row
        key={row.path}
    >
        <Table.Cell>
            <Text
                variant="monoMediumSize"
                color="high"
            >
                {row.path}
            </Text>
        </Table.Cell>
        <Table.Cell
            isNumeric={true}
            isShrink={true}
        >
            {integer.format(row.visitors)}
        </Table.Cell>
        <Table.Cell
            isNumeric={true}
            isShrink={true}
        >
            {formatDuration(row.seconds)}
        </Table.Cell>
        <Table.Cell>
            <Progress
                valueFormatter={(value) => percent.format(value / 100)}
                aria-label={t("blocks.dashboard01.columnBounce")}
                value={row.bounce * 100}
                isValueVisible={true}
                type="neutral"
                size="small"
                max={100}
            />
        </Table.Cell>
        <Table.Cell
            isShrink={true}
        >
            <Badge
                icon={row.trend > 0 ? TrendingUpIcon : TrendingDownIcon}
                type={row.trend > 0 ? "success" : "danger"}
                title={signedPercent.format(row.trend)}
                variant="tonal"
            />
        </Table.Cell>
    </Table.Row>;

    const renderCards = (first: ReactNode, second: ReactNode, firstStyle: ViewStyle, secondStyle: ViewStyle) => <View
        style={[
            isSplit ? stylesheet.row : undefined,
            {
                gap: spaces.spacingMd
            }
        ]}
    >
        <View
            style={firstStyle}
        >
            {first}
        </View>
        <View
            style={secondStyle}
        >
            {second}
        </View>
    </View>;

    return <Sidebar.Provider
        variant="inset"
    >
        <Sidebar
            collapseBehaviour={isRoomy ? "offcanvas" : "icon"}
            title={t("blocks.dashboard01.navTitle")}
        >
            <Sidebar.Header>
                <WorkspaceSwitcher
                    onPress={handlePress}
                />
            </Sidebar.Header>
            <Sidebar.Content>
                <Sidebar.Group>
                    <Sidebar.Menu>
                        {renderNavItem("dashboard", LayoutDashboardIcon, t("blocks.dashboard01.navDashboard"))}
                        {renderNavItem("analytics", ChartColumnIcon, t("blocks.dashboard01.navAnalytics"))}
                        {renderNavItem("campaigns", TargetIcon, t("blocks.dashboard01.navCampaigns"), 6)}
                        {renderNavItem("audience", UsersIcon, t("blocks.dashboard01.navAudience"))}
                        {renderNavItem("reports", FileTextIcon, t("blocks.dashboard01.navReports"))}
                    </Sidebar.Menu>
                </Sidebar.Group>
                <Sidebar.Group
                    title={t("blocks.dashboard01.channels")}
                >
                    <Sidebar.Menu>
                        {renderNavItem("websites", GlobeIcon, t("blocks.dashboard01.navWebsites"))}
                        {renderNavItem("integrations", ZapIcon, t("blocks.dashboard01.navIntegrations"))}
                    </Sidebar.Menu>
                </Sidebar.Group>
            </Sidebar.Content>
            <Sidebar.Footer>
                <Sidebar.Menu>
                    <Sidebar.MenuItem>
                        <Sidebar.MenuButton
                            renderIcon={() => <Avatar
                                title={t("blocks.dashboard01.userName")}
                                size="xSmall"
                            />}
                            subTitle={t("blocks.dashboard01.userEmail")}
                            title={t("blocks.dashboard01.userName")}
                        />
                        <Sidebar.MenuAction
                            title={t("blocks.dashboard01.userMenu")}
                            icon={EllipsisVerticalIcon}
                            onPress={handlePress}
                            isShowOnHover={false}
                        />
                    </Sidebar.MenuItem>
                </Sidebar.Menu>
            </Sidebar.Footer>
        </Sidebar>
        <Sidebar.Inset>
            <View
                style={[
                    stylesheet.bar,
                    {
                        paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                        paddingVertical: spaces.spacingMd,
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.group,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Sidebar.Trigger/>
                    {
                        isCompact ?
                            null
                        :
                            <Breadcrumb
                                accessibilityLabel={t("blocks.dashboard01.breadcrumbLabel")}
                            >
                                <Breadcrumb.List>
                                    <Breadcrumb.Item>
                                        <Breadcrumb.Link
                                            title={t("blocks.dashboard01.navAnalytics")}
                                            href="#"
                                        />
                                    </Breadcrumb.Item>
                                    <Breadcrumb.Item>
                                        <Breadcrumb.Page
                                            title={t("blocks.dashboard01.overview")}
                                        />
                                    </Breadcrumb.Item>
                                </Breadcrumb.List>
                            </Breadcrumb>
                    }
                </View>
                <View
                    style={stylesheet.grow}
                />
                {
                    isCompact ?
                        null
                    :
                        <TextInput
                            accessibilityLabel={t("blocks.dashboard01.search")}
                            placeholder={t("blocks.dashboard01.search")}
                            style={stylesheet.search}
                            spreadBehaviour="free"
                            icon={SearchIcon}
                            size="small"
                        />
                }
                <Button
                    title={isCompact ? undefined : t("blocks.dashboard01.dateRange")}
                    accessibilityLabel={t("blocks.dashboard01.dateRange")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    icon={CalendarIcon}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
                <Badge.Indicator
                    spreadBehaviour="free"
                    type="danger"
                    count={3}
                >
                    <Button
                        accessibilityLabel={t("blocks.dashboard01.notifications")}
                        spreadBehaviour="free"
                        onPress={handlePress}
                        icon={BellIcon}
                        variant="ghost"
                        type="neutral"
                        size="small"
                    />
                </Badge.Indicator>
                <Button
                    title={isCompact ? undefined : t("blocks.dashboard01.createReport")}
                    accessibilityLabel={t("blocks.dashboard01.createReport")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="filled"
                    icon={PlusIcon}
                    type="primary"
                    size="small"
                />
            </View>
            <Separator/>
            <View
                style={[
                    stylesheet.content,
                    {
                        padding: isCompact ? spaces.spacingMd : spaces.spacingLg,
                        gap: spaces.spacingLg
                    }
                ]}
            >
                {
                    tileRows.map((tileRow, index) => <View
                        style={[
                            stylesheet.row,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                        key={`tiles${index}`}
                    >
                        {tileRow.map(renderTile)}
                    </View>)
                }
                {
                    renderCards(
                        <Chart
                            description={t(`blocks.dashboard01.${range}Description`)}
                            title={t("blocks.dashboard01.visitorsTitle")}
                            spreadBehaviour="stretch"
                            isAnimatedOnMount={true}
                            style={stylesheet.chart}
                            config={visitorConfig}
                        >
                            <Chart.Range
                                options={RANGES.map((key) => ({
                                    title: t(`blocks.dashboard01.${key}`),
                                    key
                                }))}
                                title={t("blocks.dashboard01.rangeTitle")}
                                onChange={handleRangeChange}
                                value={range}
                            />
                            <AreaChart
                                categoryFormatter={(value) => dayFormat.format(new Date(String(value)))}
                                categoryTitle={t("blocks.dashboard01.date")}
                                stackBehaviour="overlay"
                                categoryKey="date"
                                plotSize="medium"
                                data={chartRows}
                                seriesKeys={[
                                    "desktop",
                                    "mobile"
                                ]}
                            />
                        </Chart>,
                        <Chart
                            description={t("blocks.dashboard01.devicesDescription")}
                            title={t("blocks.dashboard01.devices")}
                            spreadBehaviour="stretch"
                            isAnimatedOnMount={true}
                            style={stylesheet.chart}
                            config={deviceConfig}
                        >
                            <PieChart
                                valueFormatter={(value) => integer.format(value)}
                                centerTitle={t("blocks.dashboard01.sessions")}
                                categoryTitle={t("blocks.dashboard01.device")}
                                centerValue={integer.format(48210)}
                                categoryKey="name"
                                plotSize="medium"
                                valueKey="value"
                                variant="donut"
                                data={DEVICES}
                            />
                        </Chart>,
                        stylesheet.wide,
                        stylesheet.narrow
                    )
                }
                {
                    renderCards(
                        <Chart
                            description={t("blocks.dashboard01.sourcesDescription")}
                            footer={<View
                                style={[
                                    stylesheet.bar,
                                    {
                                        justifyContent: "space-between",
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <Text
                                    variant="bodyMediumSize"
                                    color="mid"
                                >
                                    {t("blocks.dashboard01.totalSessions")}
                                </Text>
                                <Text
                                    variant="titleSmallSize"
                                    color="high"
                                >
                                    {integer.format(48210)}
                                </Text>
                            </View>}
                            title={t("blocks.dashboard01.sources")}
                            spreadBehaviour="stretch"
                            isAnimatedOnMount={true}
                            style={stylesheet.chart}
                            config={sourceConfig}
                        >
                            <BarChart
                                valueFormatter={(value) => integer.format(value)}
                                categoryTitle={t("blocks.dashboard01.source")}
                                data={SOURCES.map((item) => ({
                                    sessions: item.sessions,
                                    source: t(item.source)
                                }))}
                                orientation="horizontal"
                                categoryKey="source"
                                labelBehaviour="all"
                                plotSize="medium"
                                seriesKeys={[
                                    "sessions"
                                ]}
                            />
                        </Chart>,
                        <Card
                            spreadBehaviour="stretch"
                            style={stylesheet.chart}
                            variant="outline"
                        >
                            <Card.Header>
                                <Card.Title>
                                    {t("blocks.dashboard01.pages")}
                                </Card.Title>
                                <Card.Description>
                                    {t("blocks.dashboard01.pagesDescription")}
                                </Card.Description>
                                <Card.Action>
                                    <Button
                                        title={t("blocks.dashboard01.viewAll")}
                                        onPress={handlePress}
                                        variant="outline"
                                        type="neutral"
                                        size="small"
                                    />
                                </Card.Action>
                            </Card.Header>
                            <Card.Content>
                                <Table
                                    caption={t("blocks.dashboard01.pages")}
                                    isCaptionHidden={true}
                                    size="large"
                                >
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.Head>
                                                {t("blocks.dashboard01.columnPage")}
                                            </Table.Head>
                                            <Table.Head
                                                isNumeric={true}
                                                isShrink={true}
                                            >
                                                {t("blocks.dashboard01.columnVisitors")}
                                            </Table.Head>
                                            <Table.Head
                                                isNumeric={true}
                                                isShrink={true}
                                            >
                                                {t("blocks.dashboard01.columnDuration")}
                                            </Table.Head>
                                            <Table.Head>
                                                {t("blocks.dashboard01.columnBounce")}
                                            </Table.Head>
                                            <Table.Head
                                                isShrink={true}
                                            >
                                                {t("blocks.dashboard01.columnTrend")}
                                            </Table.Head>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {PAGES.map(renderPage)}
                                    </Table.Body>
                                </Table>
                            </Card.Content>
                        </Card>,
                        stylesheet.narrow,
                        stylesheet.wide
                    )
                }
            </View>
        </Sidebar.Inset>
    </Sidebar.Provider>;
};
export default Dashboard01;
